import type {
  AffinityDefinition,
  BossDefinition,
  ContentPack,
  DefenseDefinition,
  EliteModifierDefinition,
  EnemyDefinition,
  ReactionDefinition,
  RoundDefinition,
  SpawnPatternDefinition,
  StatusDefinition,
  UpgradeDefinition,
  WeaponDefinition,
  WeaponKind,
} from "@stormglass/game-core";

const TICKS_PER_SECOND = 30;

const statuses: readonly StatusDefinition[] = [
  {
    id: "status.wet",
    schemaVersion: 1,
    stackPolicy: "max",
    maxStacks: 1,
    refreshPolicy: "refresh",
    durationTicks: 6 * TICKS_PER_SECOND,
    bossEffectiveness: 1,
    movementMultiplierPerStack: 1,
    interactionTags: ["water", "conductive"],
  },
  {
    id: "status.chill",
    schemaVersion: 1,
    stackPolicy: "add",
    maxStacks: 5,
    refreshPolicy: "refresh",
    durationTicks: 5 * TICKS_PER_SECOND,
    bossEffectiveness: 0.5,
    movementMultiplierPerStack: 0.92,
    interactionTags: ["cold"],
  },
  {
    id: "status.freeze",
    schemaVersion: 1,
    stackPolicy: "replace",
    maxStacks: 1,
    refreshPolicy: "refresh",
    durationTicks: 2 * TICKS_PER_SECOND,
    bossEffectiveness: 0.35,
    movementMultiplierPerStack: 0,
    interactionTags: ["cold", "frozen"],
  },
  {
    id: "status.pressure-drop",
    schemaVersion: 1,
    stackPolicy: "replace",
    maxStacks: 1,
    refreshPolicy: "refresh",
    durationTicks: 4 * TICKS_PER_SECOND,
    bossEffectiveness: 0.6,
    movementMultiplierPerStack: 1,
    interactionTags: ["wind", "vulnerable-area"],
  },
];

const affinities: readonly AffinityDefinition[] = [
  {
    id: "affinity.neutral",
    schemaVersion: 1,
    incomingDamageMultipliers: {},
    statusModifiers: {},
  },
  {
    id: "affinity.fire",
    schemaVersion: 1,
    incomingDamageMultipliers: { rain: 1.5, hail: 1.5 },
    statusModifiers: { "status.wet": 1.5, "status.chill": 1.5 },
  },
  {
    id: "affinity.charged",
    schemaVersion: 1,
    incomingDamageMultipliers: { lightning: 0.55, hail: 1.4 },
    statusModifiers: { "status.chill": 1.5, "status.freeze": 1.25 },
  },
  {
    id: "affinity.dust",
    schemaVersion: 1,
    incomingDamageMultipliers: { rain: 1.45, wind: 1.3 },
    statusModifiers: {},
  },
  {
    id: "affinity.frost",
    schemaVersion: 1,
    incomingDamageMultipliers: { hail: 0.55 },
    statusModifiers: { "status.chill": 0.5, "status.freeze": 0.4 },
  },
];

const enemies: readonly EnemyDefinition[] = [
  {
    id: "enemy.drifter",
    schemaVersion: 1,
    baseHealth: 16,
    speedPerSecond: 48,
    impactDamage: 1,
    chargeReward: 5,
    affinityId: "affinity.neutral",
    radius: 18,
    behavior: "radial",
    displacementResistance: 0,
    earliestRound: 1,
    spawnWeight: 10,
  },
  {
    id: "enemy.runner",
    schemaVersion: 1,
    baseHealth: 10,
    speedPerSecond: 78,
    impactDamage: 1,
    chargeReward: 5,
    affinityId: "affinity.neutral",
    radius: 15,
    behavior: "radial",
    displacementResistance: 0,
    earliestRound: 3,
    spawnWeight: 6,
  },
  {
    id: "enemy.brute",
    schemaVersion: 1,
    baseHealth: 45,
    speedPerSecond: 28,
    impactDamage: 3,
    chargeReward: 15,
    affinityId: "affinity.neutral",
    radius: 28,
    behavior: "radial",
    displacementResistance: 0.25,
    earliestRound: 4,
    spawnWeight: 3,
  },
  {
    id: "enemy.swarm-fragment",
    schemaVersion: 1,
    baseHealth: 9,
    speedPerSecond: 66,
    impactDamage: 1,
    chargeReward: 2,
    affinityId: "affinity.neutral",
    radius: 10,
    behavior: "radial",
    displacementResistance: 0,
    earliestRound: 7,
    spawnWeight: 5,
  },
  {
    id: "enemy.splitter",
    schemaVersion: 1,
    baseHealth: 72,
    speedPerSecond: 42,
    impactDamage: 2,
    chargeReward: 9,
    affinityId: "affinity.neutral",
    radius: 23,
    behavior: "split-on-death",
    splitInto: { enemyId: "enemy.swarm-fragment", count: 3 },
    displacementResistance: 0.1,
    earliestRound: 8,
    spawnWeight: 3,
  },
  {
    id: "enemy.fireling",
    schemaVersion: 1,
    baseHealth: 64,
    speedPerSecond: 54,
    impactDamage: 2,
    chargeReward: 10,
    affinityId: "affinity.fire",
    radius: 20,
    behavior: "radial",
    displacementResistance: 0,
    earliestRound: 11,
    spawnWeight: 4,
  },
  {
    id: "enemy.charged-shell",
    schemaVersion: 1,
    baseHealth: 120,
    speedPerSecond: 34,
    impactDamage: 3,
    chargeReward: 16,
    affinityId: "affinity.charged",
    radius: 27,
    behavior: "radial",
    displacementResistance: 0.35,
    earliestRound: 21,
    spawnWeight: 3,
  },
  {
    id: "enemy.anchor",
    schemaVersion: 1,
    baseHealth: 175,
    speedPerSecond: 31,
    impactDamage: 5,
    chargeReward: 23,
    affinityId: "affinity.dust",
    radius: 32,
    behavior: "radial",
    displacementResistance: 0.9,
    earliestRound: 31,
    spawnWeight: 2,
  },
];

const eliteModifiers: readonly EliteModifierDefinition[] = [
  {
    id: "elite.fast",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 1,
    healthMultiplier: 1,
    speedMultiplier: 1.4,
    mitigation: 0,
    regenerationPerSecond: 0,
    displacementResistance: 0,
  },
  {
    id: "elite.armored",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 1,
    healthMultiplier: 1.25,
    speedMultiplier: 0.9,
    mitigation: 0.25,
    regenerationPerSecond: 0,
    displacementResistance: 0.25,
  },
  {
    id: "elite.elemental",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 1,
    healthMultiplier: 1.1,
    speedMultiplier: 1,
    mitigation: 0,
    regenerationPerSecond: 0,
    displacementResistance: 0,
    affinityOverride: "affinity.fire",
  },
  {
    id: "elite.regenerating",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 0.8,
    healthMultiplier: 1.2,
    speedMultiplier: 1,
    mitigation: 0,
    regenerationPerSecond: 2.5,
    displacementResistance: 0,
  },
  {
    id: "elite.unstoppable",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 0.7,
    healthMultiplier: 1.35,
    speedMultiplier: 0.9,
    mitigation: 0.1,
    regenerationPerSecond: 0,
    displacementResistance: 1,
  },
  {
    id: "elite.shielded",
    schemaVersion: 1,
    earliestRound: 20,
    weight: 0.8,
    healthMultiplier: 1.6,
    speedMultiplier: 0.85,
    mitigation: 0.1,
    regenerationPerSecond: 0,
    displacementResistance: 0.2,
  },
];

const weapons: readonly WeaponDefinition[] = [
  {
    id: "weapon.lightning.base",
    schemaVersion: 1,
    kind: "lightning",
    behavior: "pulse-single-target",
    targetingProfile: "nearest-core",
    damageType: "lightning",
    baseStats: {
      damage: 10,
      intervalTicks: 36,
      criticalChance: 0.05,
      criticalMultiplier: 2,
      radius: 110,
      durationTicks: 0,
      statusStacks: 0,
      displacement: 0,
    },
  },
  {
    id: "weapon.rain.base",
    schemaVersion: 1,
    kind: "rain",
    behavior: "cluster-zone",
    targetingProfile: "densest-cluster",
    damageType: "rain",
    baseStats: {
      damage: 2.5,
      intervalTicks: 90,
      criticalChance: 0,
      criticalMultiplier: 1,
      radius: 120,
      durationTicks: 120,
      statusId: "status.wet",
      statusStacks: 1,
      displacement: 0,
    },
  },
  {
    id: "weapon.wind.base",
    schemaVersion: 1,
    kind: "wind",
    behavior: "gust-area",
    targetingProfile: "densest-cluster",
    damageType: "wind",
    baseStats: {
      damage: 4,
      intervalTicks: 75,
      criticalChance: 0.02,
      criticalMultiplier: 1.5,
      radius: 145,
      durationTicks: 0,
      statusStacks: 0,
      displacement: 42,
    },
  },
  {
    id: "weapon.hail.base",
    schemaVersion: 1,
    kind: "hail",
    behavior: "heavy-splash",
    targetingProfile: "elemental-priority",
    damageType: "hail",
    baseStats: {
      damage: 22,
      intervalTicks: 54,
      criticalChance: 0.04,
      criticalMultiplier: 1.75,
      radius: 75,
      durationTicks: 0,
      statusId: "status.chill",
      statusStacks: 1,
      displacement: 0,
    },
  },
];

function basic(
  id: string,
  weaponKind: WeaponKind,
  stat: string,
  operation: "add" | "multiply",
  value: number,
  baseCost: number,
): UpgradeDefinition {
  return {
    id,
    schemaVersion: 1,
    category: "basic",
    weaponKind,
    rarity: "common",
    prerequisites: [{ kind: "weapon-owned", id: weaponKind }],
    exclusions: [],
    maxLevel: 30,
    baseCost,
    costGrowth: 1.28,
    effects: [{ stat, operation, value }],
    offerWeight: 0,
    complexity: 1,
  };
}

function major(
  id: string,
  weaponKind: WeaponKind,
  feature: string,
  options: {
    readonly prerequisite?: string;
    readonly complexity?: number;
    readonly weight?: number;
    readonly guaranteedOfferRound?: number;
  } = {},
): UpgradeDefinition {
  return {
    id,
    schemaVersion: 1,
    category: "major",
    weaponKind,
    rarity: "epic",
    prerequisites: [
      { kind: "weapon-owned", id: weaponKind },
      ...(options.prerequisite === undefined
        ? []
        : [{ kind: "upgrade" as const, id: options.prerequisite }]),
    ],
    exclusions: [],
    maxLevel: 1,
    baseCost: 0,
    costGrowth: 1,
    effects: [{ stat: "feature", operation: "unlock", value: feature }],
    offerWeight: options.weight ?? 10,
    complexity: options.complexity ?? 2,
    ...(options.guaranteedOfferRound === undefined
      ? {}
      : { guaranteedOfferRound: options.guaranteedOfferRound }),
  };
}

const upgrades: readonly UpgradeDefinition[] = [
  basic(
    "upgrade.lightning.damage",
    "lightning",
    "damage",
    "multiply",
    1.22,
    50,
  ),
  basic(
    "upgrade.lightning.interval",
    "lightning",
    "intervalTicks",
    "multiply",
    0.92,
    55,
  ),
  basic(
    "upgrade.lightning.critical-chance",
    "lightning",
    "criticalChance",
    "add",
    0.02,
    65,
  ),
  basic(
    "upgrade.lightning.critical-damage",
    "lightning",
    "criticalMultiplier",
    "add",
    0.2,
    70,
  ),
  major("upgrade.lightning.chain", "lightning", "chain", {
    weight: 30,
    guaranteedOfferRound: 5,
  }),
  major("upgrade.lightning.additional-arc", "lightning", "additional-arc", {
    prerequisite: "upgrade.lightning.chain",
    complexity: 2,
  }),
  major("upgrade.lightning.overcharge", "lightning", "overcharge"),
  major("upgrade.lightning.static-field", "lightning", "static-field", {
    complexity: 3,
  }),
  major("upgrade.lightning.execution-voltage", "lightning", "execution"),

  basic("upgrade.rain.damage", "rain", "damage", "multiply", 1.16, 35),
  basic("upgrade.rain.duration", "rain", "durationTicks", "multiply", 1.14, 38),
  basic("upgrade.rain.radius", "rain", "radius", "add", 12, 42),
  basic(
    "upgrade.rain.wet-duration",
    "rain",
    "statusDuration",
    "multiply",
    1.12,
    45,
  ),
  major("upgrade.rain.downpour", "rain", "downpour"),
  major("upgrade.rain.needle-rain", "rain", "needle-rain"),
  major("upgrade.rain.rolling-front", "rain", "rolling-front", {
    complexity: 3,
  }),
  major("upgrade.rain.saturation", "rain", "saturation", { complexity: 3 }),

  basic(
    "upgrade.wind.push-force",
    "wind",
    "displacement",
    "multiply",
    1.15,
    35,
  ),
  basic("upgrade.wind.width", "wind", "radius", "add", 14, 40),
  basic(
    "upgrade.wind.frequency",
    "wind",
    "intervalTicks",
    "multiply",
    0.93,
    42,
  ),
  basic("upgrade.wind.damage", "wind", "damage", "multiply", 1.18, 36),
  major("upgrade.wind.cyclone", "wind", "cyclone"),
  major("upgrade.wind.compression-front", "wind", "compression-front"),
  major("upgrade.wind.wind-shear", "wind", "wind-shear"),
  major("upgrade.wind.pressure-drop", "wind", "pressure-drop", {
    complexity: 3,
  }),

  basic("upgrade.hail.impact-damage", "hail", "damage", "multiply", 1.2, 38),
  basic("upgrade.hail.splash-radius", "hail", "radius", "add", 10, 42),
  basic("upgrade.hail.chill-strength", "hail", "statusStacks", "add", 1, 48),
  basic(
    "upgrade.hail.frequency",
    "hail",
    "intervalTicks",
    "multiply",
    0.93,
    44,
  ),
  major("upgrade.hail.barrage", "hail", "hail-barrage"),
  major("upgrade.hail.shatter", "hail", "shatter"),
  major("upgrade.hail.glacial-impact", "hail", "glacial-impact"),
  major("upgrade.hail.deep-freeze", "hail", "deep-freeze", { complexity: 3 }),
];

const reactions: readonly ReactionDefinition[] = [
  {
    id: "reaction.thunderstorm",
    schemaVersion: 1,
    incomingDamageType: "lightning",
    requiredStatusId: "status.wet",
    requiredStatusStacks: 1,
    requiredWeaponKinds: ["rain", "lightning"],
    consumption: "none",
    bonusDamageMultiplier: 0.35,
    splashRadius: 95,
    displacement: 0,
    internalCooldownTicks: 12,
    requiresCoupler: false,
  },
  {
    id: "reaction.flash-freeze",
    schemaVersion: 1,
    incomingDamageType: "hail",
    requiredStatusId: "status.chill",
    requiredStatusStacks: 3,
    requiredWeaponKinds: ["rain", "hail"],
    consumption: "all-stacks",
    bonusDamageMultiplier: 0.2,
    splashRadius: 65,
    displacement: 0,
    applyStatusId: "status.freeze",
    applyStatusStacks: 1,
    internalCooldownTicks: 60,
    requiresCoupler: false,
  },
  {
    id: "reaction.hailstorm",
    schemaVersion: 1,
    incomingDamageType: "hail",
    requiredWeaponKinds: ["wind", "hail"],
    consumption: "none",
    bonusDamageMultiplier: 0.25,
    splashRadius: 150,
    displacement: 25,
    internalCooldownTicks: 24,
    requiresCoupler: true,
  },
  {
    id: "reaction.squall-line",
    schemaVersion: 1,
    incomingDamageType: "wind",
    requiredStatusId: "status.wet",
    requiredStatusStacks: 1,
    requiredWeaponKinds: ["rain", "wind"],
    consumption: "none",
    bonusDamageMultiplier: 0.15,
    splashRadius: 135,
    displacement: 32,
    internalCooldownTicks: 20,
    requiresCoupler: true,
    zoneBehavior: "move-rain",
  },
  {
    id: "reaction.ion-cyclone",
    schemaVersion: 1,
    incomingDamageType: "lightning",
    requiredWeaponKinds: ["lightning", "wind"],
    consumption: "none",
    bonusDamageMultiplier: 0.3,
    splashRadius: 125,
    displacement: 14,
    internalCooldownTicks: 20,
    requiresCoupler: true,
  },
];

const spawnPatterns: readonly SpawnPatternDefinition[] = [
  {
    id: "spawn.scattered-trickle",
    schemaVersion: 1,
    behavior: "scattered",
    earliestRound: 1,
  },
  {
    id: "spawn.clustered-front",
    schemaVersion: 1,
    behavior: "clustered",
    earliestRound: 11,
  },
  {
    id: "spawn.partial-ring-pulse",
    schemaVersion: 1,
    behavior: "ring",
    earliestRound: 21,
  },
  {
    id: "spawn.alternating-fronts",
    schemaVersion: 1,
    behavior: "alternating",
    earliestRound: 26,
  },
  {
    id: "spawn.spiral-entry",
    schemaVersion: 1,
    behavior: "spiral",
    earliestRound: 31,
  },
  {
    id: "spawn.portal-burst",
    schemaVersion: 1,
    behavior: "portal",
    earliestRound: 36,
  },
  {
    id: "spawn.mixed-director",
    schemaVersion: 1,
    behavior: "mixed",
    earliestRound: 41,
  },
];

const bosses: readonly BossDefinition[] = [
  {
    id: "boss.commander.v1",
    schemaVersion: 1,
    movementProfile: "timed-direct",
    durationTicks: 1800,
    baseHealth: 1800,
    healthScalePerTier: 2.1,
    impactDamage: 20,
    affinityId: "affinity.neutral",
    rewardCharge: 180,
    rewardCondensate: 12,
    abilities: [
      {
        kind: "summon",
        triggerRemainingTicks: 1200,
        parameters: {
          groups: [
            { enemyId: "enemy.drifter", count: 2 },
            { enemyId: "enemy.runner", count: 2 },
          ],
        },
      },
      {
        kind: "summon",
        triggerRemainingTicks: 600,
        parameters: {
          groups: [
            { enemyId: "enemy.brute", count: 2 },
            { enemyId: "enemy.runner", count: 4 },
          ],
        },
      },
    ],
  },
  {
    id: "boss.bulwark.v1",
    schemaVersion: 1,
    movementProfile: "timed-direct",
    durationTicks: 1800,
    baseHealth: 4300,
    healthScalePerTier: 2.1,
    impactDamage: 20,
    affinityId: "affinity.neutral",
    rewardCharge: 300,
    rewardCondensate: 20,
    abilities: [
      {
        kind: "mitigation",
        triggerRemainingTicks: 1200,
        parameters: { value: 0.2 },
      },
      {
        kind: "mitigation",
        triggerRemainingTicks: 600,
        parameters: { value: 0.35 },
      },
    ],
  },
  {
    id: "boss.disruptor.v1",
    schemaVersion: 1,
    movementProfile: "timed-direct",
    durationTicks: 1800,
    baseHealth: 6900,
    healthScalePerTier: 2.1,
    impactDamage: 20,
    affinityId: "affinity.charged",
    rewardCharge: 450,
    rewardCondensate: 28,
    abilities: [
      {
        kind: "weapon-slow",
        triggerRemainingTicks: 1200,
        parameters: { durationTicks: 150, intervalMultiplier: 1.5 },
      },
      {
        kind: "weapon-slow",
        triggerRemainingTicks: 600,
        parameters: { durationTicks: 150, intervalMultiplier: 1.5 },
      },
    ],
  },
];

function patternForRound(round: number): string {
  if (round >= 41) return "spawn.mixed-director";
  if (round >= 36) return "spawn.portal-burst";
  if (round >= 31) return "spawn.spiral-entry";
  if (round >= 26) return "spawn.alternating-fronts";
  if (round >= 21) return "spawn.partial-ring-pulse";
  if (round >= 11) return "spawn.clustered-front";
  return "spawn.scattered-trickle";
}

function enemyWeightsForRound(round: number): Record<string, number> {
  return Object.fromEntries(
    enemies
      .filter((enemy) => enemy.earliestRound <= round)
      .map((enemy) => [enemy.id, enemy.spawnWeight]),
  );
}

const bossRotation = [
  "boss.commander.v1",
  "boss.bulwark.v1",
  "boss.disruptor.v1",
] as const;
const rounds: readonly RoundDefinition[] = Array.from(
  { length: 60 },
  (_, index) => {
    const round = index + 1;
    const bossId =
      round % 10 === 0
        ? bossRotation[(round / 10 - 1) % bossRotation.length]
        : undefined;
    return {
      round,
      quota: 8 + Math.floor((round - 1) * 2.2),
      spawnIntervalTicks: Math.max(15, 60 - Math.floor(round / 2)),
      spawnPatternId: patternForRound(round),
      enemyWeights: enemyWeightsForRound(round),
      ...(bossId === undefined ? {} : { bossId }),
    };
  },
);

const defenses: readonly DefenseDefinition[] = [
  {
    id: "defense.weather-shield",
    schemaVersion: 1,
    kind: "shield",
    cost: 40,
    unlockRound: 40,
    parameters: {
      capacity: 6,
      rechargeDelayTicks: 150,
      rechargePerSecond: 0.04,
    },
  },
  {
    id: "defense.integrity-reinforcement",
    schemaVersion: 1,
    kind: "integrity",
    cost: 40,
    unlockRound: 40,
    parameters: { amount: 5 },
  },
  {
    id: "defense.repulsion-system",
    schemaVersion: 1,
    kind: "repulsion",
    cost: 40,
    unlockRound: 40,
    parameters: { radius: 210, displacement: 55, cooldownTicks: 240 },
  },
];

function byId<T extends { readonly id: string }>(
  definitions: readonly T[],
): Readonly<Record<string, T>> {
  return Object.fromEntries(
    definitions.map((definition) => [definition.id, definition]),
  );
}

export const rawLaunchContentPack: ContentPack = {
  schemaVersion: 1,
  contentVersion: "1.0.0",
  simulationVersion: 1,
  enemies: byId(enemies),
  eliteModifiers: byId(eliteModifiers),
  weapons: byId(weapons),
  upgrades: byId(upgrades),
  statuses: byId(statuses),
  affinities: byId(affinities),
  reactions: byId(reactions),
  spawnPatterns: byId(spawnPatterns),
  bosses: byId(bosses),
  rounds,
  defenses: byId(defenses),
  economy: {
    passiveChargePerSecond: 0.5,
    firstWeatherCost: 25,
    condensatePerRound: 0.75,
    condensatePerBoss: 3,
    firstClearBonus: { 10: 25, 20: 25, 30: 25, 40: 40, 50: 40, 60: 40 },
  },
};
